/**
 * Automatic Popup Component with Cookie Support
 *
 * This component will:
 * 1. Show automatically when the page loads
 * 2. Set a cookie when the user clicks any button
 * 3. Not show again if the cookie exists
 * 4. Still collect data from simple forms on submission
 *
 * Path: source/js/popup/popup.component.js
 */

class PopupComponent {
  constructor(options = {}) {
    // Default configuration
    this.config = {
      cookieName: options.cookieName || 'popup_dismissed',
      cookieDuration: options.cookieDuration || 30, // days
      signUpUrl: options.pageUrl || '/sign-up',
      modalTitle: options.modalTitle || 'Would you like to register?',
      modalMessage: options.modalMessage || 'Click "Sign Up" to create your account or "No, thanks" to continue.',
      signUpButtonText: options.signUpButtonText || 'Sign Up',
      noThanksButtonText: options.noThanksButtonText || 'No, thanks',
      showDelay: options.showDelay || 1000, // milliseconds to wait before showing popup

      // We still track these for the form handling
      footerFormSelectors: options.footerFormSelectors || '.contact-form, .subscribe-form',
      emailFieldName: options.emailFieldName || 'email',
      nameFieldName: options.nameFieldName || 'name'
    };

    // For storing form data
    this.formData = null;

    // Element references
    this.overlay = null;
    this.container = null;
    this.closeButton = null;
    this.contentArea = null;

    // Bind methods to maintain 'this' context
    this.openPopup = this.openPopup.bind(this);
    this.closePopup = this.closePopup.bind(this);
    this.handleFooterSubmit = this.handleFooterSubmit.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.setCookie = this.setCookie.bind(this);
    this.getCookie = this.getCookie.bind(this);
    this.handleSignUpClick = this.handleSignUpClick.bind(this);
    this.handleNoThanksClick = this.handleNoThanksClick.bind(this);

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  /**
   * Initialize the component
   */
  init() {
    // Create popup elements
    this.createPopupElements();

    // Add required styles
    this.addStyles();

    // Register event listeners for the forms
    this.bindFormEvents();

    // Check if we should show the popup
    if (!this.getCookie(this.config.cookieName)) {
      // Set a timeout to show the popup after delay
      setTimeout(() => {
        this.openPopup();
      }, this.config.showDelay);
    }
  }

  /**
   * Create the popup HTML structure
   */
  createPopupElements() {
    // Create elements if they don't exist
    if (!document.getElementById('confirmation-modal-overlay')) {
      // Create overlay
      this.overlay = document.createElement('div');
      this.overlay.id = 'confirmation-modal-overlay';
      this.overlay.className = 'confirmation-modal-overlay';

      // Create container
      this.container = document.createElement('div');
      this.container.id = 'confirmation-modal-container';
      this.container.className = 'confirmation-modal-container';

      // Create header
      const header = document.createElement('div');
      header.className = 'confirmation-modal-header';

      // Create close button
      this.closeButton = document.createElement('button');
      this.closeButton.id = 'confirmation-modal-close';
      this.closeButton.className = 'confirmation-modal-close';
      this.closeButton.innerHTML = '&times;';
      this.closeButton.addEventListener('click', this.handleNoThanksClick);

      // Create content area
      this.contentArea = document.createElement('div');
      this.contentArea.id = 'confirmation-modal-content';
      this.contentArea.className = 'confirmation-modal-content';

      // Create title
      const title = document.createElement('h3');
      title.textContent = this.config.modalTitle;

      // Create message
      const message = document.createElement('p');
      message.textContent = this.config.modalMessage;

      // Create buttons container
      const buttonsContainer = document.createElement('div');
      buttonsContainer.className = 'confirmation-modal-buttons';

      // Create Sign Up button
      const signUpButton = document.createElement('button');
      signUpButton.id = 'confirmation-signup-button';
      signUpButton.className = 'btn btn-primary';
      signUpButton.textContent = this.config.signUpButtonText;
      signUpButton.addEventListener('click', this.handleSignUpClick);

      // Create No Thanks button
      const noThanksButton = document.createElement('button');
      noThanksButton.id = 'btn btn-secondary';
      noThanksButton.className = 'confirmation-button-secondary';
      noThanksButton.textContent = this.config.noThanksButtonText;
      noThanksButton.addEventListener('click', this.handleNoThanksClick);

      // Assemble buttons
      buttonsContainer.appendChild(signUpButton);
      buttonsContainer.appendChild(noThanksButton);

      // Assemble the modal
      header.appendChild(this.closeButton);
      this.contentArea.appendChild(title);
      this.contentArea.appendChild(message);
      this.contentArea.appendChild(buttonsContainer);
      this.container.appendChild(header);
      this.container.appendChild(this.contentArea);
      this.overlay.appendChild(this.container);

      // Add to body
      document.body.appendChild(this.overlay);

      // Add event listeners
      this.overlay.addEventListener('click', this.handleOverlayClick);
      document.addEventListener('keyup', this.handleKeyPress);
    } else {
      // If elements already exist, get references
      this.overlay = document.getElementById('confirmation-modal-overlay');
      this.container = document.getElementById('confirmation-modal-container');
      this.closeButton = document.getElementById('confirmation-modal-close');
      this.contentArea = document.getElementById('confirmation-modal-content');

      // Make sure event listeners are set
      document.getElementById('confirmation-signup-button').addEventListener('click', this.handleSignUpClick);
      document.getElementById('confirmation-no-thanks-button').addEventListener('click', this.handleNoThanksClick);
      this.closeButton.addEventListener('click', this.handleNoThanksClick);
    }
  }

  /**
   * Add required CSS styles
   */
  addStyles() {
    // Check if styles are already added
    if (!document.getElementById('confirmation-modal-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'confirmation-modal-styles';
      styleElement.textContent = `
        .confirmation-modal-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.7);
          z-index: 9999;
          justify-content: center;
          align-items: center;
        }

        .confirmation-modal-container {
          background-color: #fff;
          border-radius: 5px;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
          width: 90%;
          max-width: 400px;
          position: relative;
          padding: 30px;
        }

        .confirmation-modal-header {
          position: absolute;
          top: 10px;
          right: 10px;
        }

        .confirmation-modal-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }

        .confirmation-modal-content {
          text-align: center;
        }

        .confirmation-modal-content h3 {
          margin-top: 0;
          margin-bottom: 15px;
        }

        .confirmation-modal-buttons {
          display: flex;
          justify-content: center;
          gap: 15px;
          margin-top: 25px;
        }

        .confirmation-button-primary {
          padding: 10px 20px;
          background-color: #4CAF50;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
        }

        .confirmation-button-primary:hover {
          background-color: #45a049;
        }

        .confirmation-button-secondary {
          padding: 10px 20px;
          background-color: #f5f5f5;
          color: #333;
          border: 1px solid #ccc;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
        }

        .confirmation-button-secondary:hover {
          background-color: #e9e9e9;
        }

        /* Prevent body scrolling when popup is open */
        body.confirmation-modal-open {
          overflow: hidden;
        }
      `;
      document.head.appendChild(styleElement);
    }
  }

  /**
   * Bind event listeners to forms
   */
  bindFormEvents() {
    // Footer form submit
    const footerForms = document.querySelectorAll(this.config.footerFormSelectors);
    footerForms.forEach(form => {
      form.addEventListener('submit', this.handleFooterSubmit);
    });
  }

  /**
   * Handle Sign Up button click
   */
  handleSignUpClick() {
    // Set the cookie so popup doesn't appear again
    this.setCookie(this.config.cookieName, 'true', this.config.cookieDuration);

    // Build the URL with query parameters if we have form data
    let url = this.config.signUpUrl;

    if (this.formData) {
      // Start with a question mark
      url += '?';

      // Add email parameter if we have it
      if (this.formData.email) {
        url += 'email=' + encodeURIComponent(this.formData.email);
      }

      // Add first name parameter if we have it
      if (this.formData.firstName) {
        // Add & if we already added a parameter
        if (url.charAt(url.length - 1) !== '?') {
          url += '&';
        }
        url += 'first_name=' + encodeURIComponent(this.formData.firstName);
      }

      // Add last name parameter if we have it
      if (this.formData.lastName) {
        // Add & if we already added a parameter
        if (url.charAt(url.length - 1) !== '?') {
          url += '&';
        }
        url += 'last_name=' + encodeURIComponent(this.formData.lastName);
      }
    }

    // Navigate to the URL
    window.location.href = url;
  }

  /**
   * Handle No Thanks button click
   */
  handleNoThanksClick() {
    // Set the cookie so popup doesn't appear again
    this.setCookie(this.config.cookieName, 'true', this.config.cookieDuration);

    // Close the popup
    this.closePopup();
  }

  /**
   * Open the popup
   */
  openPopup() {
    this.overlay.style.display = 'flex';
    document.body.classList.add('confirmation-modal-open');
  }

  /**
   * Close the popup
   */
  closePopup() {
    this.overlay.style.display = 'none';
    document.body.classList.remove('confirmation-modal-open');
  }

  /**
   * Handle footer form submit
   * @param {Event} event - Submit event
   */
  handleFooterSubmit(event) {
    event.preventDefault();

    // Create an object to store form data
    const formData = {};

    // Get the email from the form
    const emailInput = event.target.querySelector(`input[type="email"], input[name="${this.config.emailFieldName}"]`);
    if (emailInput && emailInput.value) {
      formData.email = emailInput.value;
    }

    // Get the name from the form (if it exists)
    const nameInput = event.target.querySelector(`input[name="${this.config.nameFieldName}"], input[placeholder="Name"], input.name-field`);
    if (nameInput && nameInput.value) {
      // Split the name on the first space
      const fullName = nameInput.value.trim();
      const nameParts = fullName.split(' ');

      if (nameParts.length > 1) {
        // If there's at least one space, use the first part as first name
        formData.firstName = nameParts[0];

        // Use the rest as last name (joining with space if there are multiple words)
        formData.lastName = nameParts.slice(1).join(' ');
      } else {
        // If no space, use the whole thing as first name
        formData.firstName = fullName;
      }
    }

    // Store the form data in the instance for later use
    this.formData = formData;

    // Submit the form to its original action if needed
    if (event.target.action) {
      // You can submit the form normally here if needed
      // event.target.submit();
    }

    // Open the popup if cookie is not set
    if (!this.getCookie(this.config.cookieName)) {
      this.openPopup();
    } else {
      // If cookie is set but user submitted a form, redirect directly
      this.handleSignUpClick();
    }
  }

  /**
   * Handle key press (close on ESC)
   * @param {Event} event - Keyup event
   */
  handleKeyPress(event) {
    if (event.key === 'Escape') {
      this.handleNoThanksClick();
    }
  }

  /**
   * Handle overlay click (close when clicking outside form)
   * @param {Event} event - Click event
   */
  handleOverlayClick(event) {
    if (event.target === this.overlay) {
      this.handleNoThanksClick();
    }
  }

  /**
   * Set a cookie
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Days until expiration
   */
  setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
  }

  /**
   * Get a cookie by name
   * @param {string} name - Cookie name
   * @returns {string|null} - Cookie value or null if not found
   */
  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    return null;
  }
}

// Export the component
export default PopupComponent;
